import { useEffect, useMemo } from 'react';

import { usePermissions } from '@sbu/hooks.permissions';

import { entityPermissions } from 'app/permissions';
import {
  EntitiesByListIdQueryOperationQuery as EntitiesByListIdQueryOperation,
  EntitiesByListIdQueryOperationQueryVariables as EntitiesByListIdQueryOperationVariables,
  EntityInterface,
} from 'core/graphql';
import { useLazyQuery } from 'shared/hooks';
import { ENTITIES_BY_LIST_ID_QUERY } from 'shared/queries/queries';

export type EntityReputationData = Pick<
  EntityInterface,
  'reputation' | 'reputationSources'
>;
export type EntityReputationsById = Record<string, EntityReputationData>;

export const useEntityReputationsById = (entityIds: string[]) => {
  const { hasAny: hasEntityPermissions } = usePermissions(entityPermissions);

  const [getEntitiesByListIds, { data: entityReputationData }] = useLazyQuery<
    EntitiesByListIdQueryOperation,
    EntitiesByListIdQueryOperationVariables
  >(ENTITIES_BY_LIST_ID_QUERY);

  useEffect(() => {
    if (entityIds.length && hasEntityPermissions) {
      getEntitiesByListIds({
        variables: {
          entityIds,
        },
      });
    }
  }, [entityIds, hasEntityPermissions, getEntitiesByListIds]);

  const entityReputationsById: EntityReputationsById = useMemo(() => {
    return (
      entityReputationData?.entitiesByListIds?.reduce(
        (acc, entity) => ({
          ...acc,
          [entity.id]: {
            reputation: entity.reputation,
            reputationSources: entity.reputationSources,
          },
        }),
        {} as EntityReputationsById
      ) ?? {}
    );
  }, [entityReputationData]);

  return entityReputationsById;
};
